import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRoughBodyStore } from '../stores/useRoughBodyStore';
import { usePieceStore } from '../stores/usePieceStore';
import { useAuthStore } from '../stores/useAuthStore';
import { BottomDock } from '../components/Layout/BottomDock';
import { ChevronLeft, Scissors, CheckCircle, Clock, User } from 'lucide-react';
import { clsx } from 'clsx';
import { StatusBadge } from '../components/UI/StatusBadge';
import { LeftoverFabricModal } from './LeftoverFabricModal';

export const RoughBodyDetail: React.FC = () => {
    const { qrCode } = useParams();
    const navigate = useNavigate();
    const { roughBodies, fetchRoughBodies, updateStationStatus, markAsDelivered, loading } = useRoughBodyStore();
    const { tailors, fetchTailors } = usePieceStore();
    const { userProfile } = useAuthStore();
    const [selectedTailorId, setSelectedTailorId] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [showLeftoverModal, setShowLeftoverModal] = useState(false);
    const [leftoverFabric, setLeftoverFabric] = useState<Array<{name: string, qty: number, uom: string, leftover: number}>>([]);

    // Initial fetch
    useEffect(() => {
        if (!roughBodies.find(p => p.qr_code === qrCode)) fetchRoughBodies();
        // Fetch tailors for all users except cutting-only roles
        if (userProfile?.role !== 'cutting') {
            fetchTailors();
        }
    }, [qrCode, userProfile?.role]);

    // Find the current piece
    const piece = roughBodies.find(p => p.qr_code === qrCode);

    if (!piece) {
        return <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6 text-slate-400 font-bold">Piece Not Found</div>;
    }

    const { status } = piece;

    // Check if user can take actions (not QC/IQ)
    const canTakeActions = userProfile?.role !== 'qc' && userProfile?.role !== 'inline_qc';

    // Station Check
    const handleStationAction = async (stage: 'cutting' | 'sewing', action: 'start' | 'complete', payload?: any) => {
        if (!canTakeActions) {
            alert('You can only view this piece. Actions are restricted to your role.');
            return;
        }
        setActionLoading(true);
        try {
            await updateStationStatus(piece.id, stage, action, payload);
        } catch (e) {
            console.error(e);
        } finally {
            setActionLoading(false);
        }
    };

    const handleSewingIn = async () => {
        if (!selectedTailorId) {
            alert("Please select a tailor first");
            return;
        }
        const tailor = tailors.find(t => t.id === selectedTailorId);
        await handleStationAction('sewing', 'start', {
            assigned_tailor_id: selectedTailorId,
            assigned_tailor_name: tailor?.full_name
        });
    }

    const isDone = (timeStr?: string) => !!timeStr;
    const timeDisplay = (timeStr?: string) => timeStr ? new Date(timeStr).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }) : '--';

    return (
        <div className="min-h-screen bg-stone-50 flex flex-col font-sans pb-safe">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-2xl pt-safe px-5 pb-3 border-b border-slate-100/80 sticky top-0 z-40">
                <div className="flex items-center gap-3 py-3">
                    <button onClick={() => navigate('/rough-body')} className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 active:bg-slate-200 transition-colors">
                        <ChevronLeft size={18} />
                    </button>
                    <div className="flex-1">
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none">{piece.qr_code}</p>
                        <h1 className="text-lg font-bold text-slate-800 leading-tight">{piece.styles?.style_code}</h1>
                    </div>
                    <div className="px-2 py-1 bg-slate-100 rounded text-xs font-bold text-slate-600 uppercase">
                        {status}
                    </div>
                </div>

                {/* Info Card - Added as requested */}
                <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase">Fabric Type</p>
                        <p className="font-semibold text-slate-800">{piece.fabric_type || 'N/A'}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase">Required Qty</p>
                        <p className="font-semibold text-slate-800">{piece.fabric_qty ? `${piece.fabric_qty} ${piece.fabric_uom}` : 'N/A'}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase">Size</p>
                        <p className="font-semibold text-slate-800">{piece.size || 'N/A'}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase">Master Notes</p>
                        <p className="font-semibold text-slate-800 truncate">{piece.pattern_master_notes || '-'}</p>
                    </div>
                </div>
            </div>

            {/* CONTENT */}
            <div className="flex-1 px-5 pt-6 pb-32 animate-fade-in space-y-6">

                {/* CUTTING STATION */}
                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Scissors size={32} />
                        </div>
                        <h3 className="text-lg font-bold text-slate-800">Cutting Station</h3>
                        <p className="text-xs text-slate-400 mt-1">Has the pattern been cut?</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-50">
                        <div className="text-center">
                            <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">In Time</p>
                            <p className="font-mono text-xs font-bold text-slate-700 mb-3">{timeDisplay(piece.cutting_started_at)}</p>
                            <button
                                onClick={() => handleStationAction('cutting', 'start', {})}
                                disabled={loading || !!piece.cutting_started_at || !canTakeActions}
                                className={clsx(
                                    "w-full py-2.5 rounded-xl text-xs font-bold border transition-all",
                                    piece.cutting_started_at ? "bg-emerald-50 text-emerald-600 border-emerald-100 cursor-default" : 
                                    !canTakeActions ? "bg-slate-50 text-slate-300 cursor-not-allowed" : "bg-blue-600 text-white active:scale-95"
                                )}
                            >
                                {piece.cutting_started_at ? 'Done' : 'Mark In'}
                            </button>
                        </div>
                        <div className="text-center">
                            <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">Out Time</p>
                            <p className="font-mono text-xs font-bold text-slate-700 mb-3">{timeDisplay(piece.cutting_completed_at)}</p>
                            <button
                                onClick={() => {
                                    if (piece.fabric_issued && !piece.cutting_completed_at) {
                                        // Show leftover modal
                                        const fabrics = Array.isArray(piece.fabric_issued) ? piece.fabric_issued : [];
                                        setLeftoverFabric(fabrics.map((f: any) => ({
                                            name: f.name || 'Fabric',
                                            qty: f.qty || 0,
                                            uom: f.uom || 'metre',
                                            leftover: 0
                                        })));
                                        setShowLeftoverModal(true);
                                    } else {
                                        handleStationAction('cutting', 'complete', {});
                                    }
                                }}
                                disabled={loading || !piece.cutting_started_at || !!piece.cutting_completed_at || !canTakeActions}
                                className={clsx(
                                    "w-full py-2.5 rounded-xl text-xs font-bold border transition-all",
                                    piece.cutting_completed_at ? "bg-emerald-50 text-emerald-600 border-emerald-100 cursor-default" :
                                        !piece.cutting_started_at || !canTakeActions ? "bg-slate-50 text-slate-300 cursor-not-allowed" : "bg-blue-600 text-white active:scale-95"
                                )}
                            >
                                {piece.cutting_completed_at ? 'Done' : 'Mark Out'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* SEWING STATION - Only show if not cutting user */}
                {userProfile?.role !== 'cutting' && (
                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Scissors size={32} />
                        </div>
                        <h3 className="text-lg font-bold text-slate-800">Sewing Station</h3>
                        <p className="text-xs text-slate-400 mt-1">Stitching progress</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-50">
                        <div className="col-span-2 space-y-3">
                            <p className="text-[10px] uppercase font-bold text-slate-400">Assigned Tailor</p>
                            {piece.sewing_started_at ? (
                                <div className="flex items-center gap-2 bg-purple-50 p-3 rounded-xl border border-purple-100 text-purple-700 font-bold text-sm">
                                    <User size={16} />
                                    {piece.assigned_tailor_name || 'Unassigned'}
                                </div>
                            ) : (
                                <select
                                    value={selectedTailorId}
                                    onChange={e => setSelectedTailorId(e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                                >
                                    <option value="">Select Tailor...</option>
                                    {tailors.map((t: any) => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                                </select>
                            )}
                        </div>

                        <div className="text-center">
                            <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">In Time</p>
                            <p className="font-mono text-xs font-bold text-slate-700 mb-3">{timeDisplay(piece.sewing_started_at)}</p>
                            <button
                                onClick={handleSewingIn}
                                disabled={loading || !!piece.sewing_started_at || !canTakeActions}
                                className={clsx(
                                    "w-full py-2.5 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-2",
                                    piece.sewing_started_at ? "bg-emerald-50 text-emerald-600 border-emerald-100 cursor-default" : 
                                    !canTakeActions ? "bg-slate-50 text-slate-300 cursor-not-allowed" : "bg-purple-600 text-white active:scale-95"
                                )}
                            >
                                {piece.sewing_started_at ? 'Done' : 'Mark In'}
                            </button>
                        </div>
                        <div className="text-center">
                            <p className="text-[10px] uppercase font-bold text-slate-400 mb-1">Out Time</p>
                            <p className="font-mono text-xs font-bold text-slate-700 mb-3">{timeDisplay(piece.sewing_completed_at)}</p>
                            <button
                                onClick={() => handleStationAction('sewing', 'complete', {})}
                                disabled={loading || !piece.sewing_started_at || !!piece.sewing_completed_at || !canTakeActions}
                                className={clsx(
                                    "w-full py-2.5 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-2",
                                    piece.sewing_completed_at ? "bg-emerald-50 text-emerald-600 border-emerald-100 cursor-default" :
                                        !piece.sewing_started_at || !canTakeActions ? "bg-slate-50 text-slate-300 cursor-not-allowed" : "bg-purple-600 text-white active:scale-95"
                                )}
                            >
                                {piece.sewing_completed_at ? 'Done' : 'Mark Out'}
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {/* HANDOVER STATION */}
                {piece.sewing_completed_at && (
                    <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                                <CheckCircle size={32} />
                            </div>
                            <h3 className="text-lg font-bold text-slate-800">Final Handover</h3>
                            <p className="text-xs text-slate-400 mt-1">Deliver back to Pattern Master</p>
                        </div>
                        <div className="pt-4 border-t border-slate-50">
                            <button
                                onClick={() => {
                                    if (!canTakeActions) {
                                        alert('You can only view this piece. Actions are restricted to your role.');
                                        return;
                                    }
                                    markAsDelivered(piece.id);
                                }}
                                disabled={loading || !!piece.delivered_at || piece.status === 'Completed' || !canTakeActions}
                                className={clsx(
                                    "w-full py-3.5 rounded-xl text-sm font-bold border transition-all flex items-center justify-center gap-2",
                                    (piece.delivered_at || piece.status === 'Completed') ? "bg-emerald-50 text-emerald-600 border-emerald-100 cursor-default" : 
                                    !canTakeActions ? "bg-slate-50 text-slate-300 cursor-not-allowed" : "bg-emerald-600 text-white active:scale-95 shadow-md shadow-emerald-600/20"
                                )}
                            >
                                {(piece.delivered_at || piece.status === 'Completed') ? 'Delivered' : 'Mark as Delivered'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <BottomDock />

            {/* Leftover Fabric Modal */}
            {showLeftoverModal && (
                <LeftoverFabricModal
                    fabrics={leftoverFabric}
                    onUpdate={setLeftoverFabric}
                    onConfirm={async () => {
                        // Save leftover data and complete cutting
                        const leftoverData = leftoverFabric.map(f => ({
                            name: f.name,
                            qty: f.qty,
                            uom: f.uom,
                            leftover: f.leftover,
                            used: f.qty - f.leftover
                        }));
                        await handleStationAction('cutting', 'complete', { fabric_leftover: leftoverData });
                        setShowLeftoverModal(false);
                    }}
                    onCancel={() => setShowLeftoverModal(false)}
                />
            )}
        </div >
    );
};
